import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import ls from 'localstorage-slim';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';
import Loading from '../GeneralComponents/Loading';

function CodeDetails() {
  const itemCode = useParams().itemCode;
  const codeType = useParams().codeType;
  const { t, i18n } = useTranslation();

  const [codeDetails, setCodeDetails] = useState({});
  const [categories, setCategories] = useState([]);
  const [arabicCategories, setArabicCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const subDomain = ls.get('subDomain');

  useEffect(() => {
    async function fetchData() {
      let documentDetailsToast = toast.loading('Please wait...');
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }
        if (!ls.get('ETAToken')) await getETAToken();

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/code_details`,
          {
            params: {
              accessToken: ls.get('ETAToken'),
              itemCode,
              codeType,
              taxpayer: ls.get('taxpayer_id'),
            },
          }
        );

        setCodeDetails(response.data);
        setCategories(
          Object.entries(response.data.codeCategorization).map(([key, value], i) => value['name'])
        );
        setArabicCategories(
          Object.entries(response.data.codeCategorization).map(([key, value], i) => value['nameAr'])
        );
        setIsLoading(false);
        toast.update(documentDetailsToast, {
          render: 'Code Details retrieved successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
          toast.update(documentDetailsToast, {
            render: `${error.response.data}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
          console.log(error.response.data);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
          toast.update(documentDetailsToast, {
            render: `${error.request}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
          toast.update(documentDetailsToast, {
            render: `${error.message}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        }
      }
    }

    fetchData();
  }, []);

  return isLoading ? (
    <Loading />
  ) : (
    <>
      <div className='container-fluid'>
        <div className='shadow m-3 p-2'>
          <div
            className={`row ${
              i18n.language === 'en' ? 'justify-content-start' : 'justify-content-end'
            } fs-4 mx-4 my-3`}>
            <span className={`${i18n.language === 'en' ? 'text-start' : 'text-end'}`}>
              {i18n.language === 'en' ? (
                <>
                  <strong>{codeDetails.itemCode}</strong> {codeDetails.codeName}
                </>
              ) : (
                <>
                  {codeDetails.codeNameAr} <strong>{codeDetails.itemCode}</strong>
                </>
              )}
            </span>
          </div>
          <div
            className={`row ${
              i18n.language === 'en' ? 'text-start' : 'text-end'
            } mx-4 fs-4 mb-4 text-decoration-underline`}
            style={{ textUnderlineOffset: '10px' }}>
            <span>{t('code.details.title')}</span>
          </div>
          <div className='row mx-4 mb-4'>
            <div className={`${i18n.language === 'en' ? 'order-1' : 'order-4'} col`}>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mb-2`}>
                <span>{t('code.details.type')}</span>
              </div>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row`}>
                <span>{codeDetails.codeTypeName}</span>
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-2' : 'order-3'} col`}>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mb-2`}>
                <span>{t('code.details.unique')}</span>
              </div>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row`}>
                <span>{codeDetails.itemCode}</span>
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-3' : 'order-2'} col`}>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mb-2`}>
                <span>{t('code.details.codeName')}</span>
              </div>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row`}>
                <span>{codeDetails.codeName}</span>
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-4' : 'order-1'} col`}>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mb-2`}>
                <span>{t('code.details.codeNameAr')}</span>
              </div>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row`}>
                <span>{codeDetails.codeNameAr}</span>
              </div>
            </div>
          </div>
          <div className='row mx-4 mb-4'>
            <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col`}>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mb-2`}>
                <span>{t('code.details.description')}</span>
              </div>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row`}>
                <span className='truncatedText'>{codeDetails.description}</span>
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col`}>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mb-2`}>
                <span>{t('code.details.descriptionAr')}</span>
              </div>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row`}>
                <span className='truncatedText' dir='rtl'>
                  {codeDetails.descriptionAr}
                </span>
              </div>
            </div>
          </div>
          <div className='row mx-4 mb-4'>
            <div className='col'>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mb-2`}>
                <span>{t('code.details.category')}</span>
              </div>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row`}>
                <span>
                  {i18n.language === 'ar' ? arabicCategories.join(' > ') : categories.join(' > ')}
                </span>
              </div>
            </div>
          </div>
          <div className='row mx-4 mb-4'>
            <div className={`${i18n.language === 'en' ? 'order-1' : 'order-3'} col-3`}>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mb-2`}>
                <span>{t('code.details.activeFrom')}</span>
              </div>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row`}>
                <span>{moment(codeDetails.activeFrom).format('DD/MM/YYYY')}</span>
              </div>
            </div>
            <div className='order-2 col-3'>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mb-2`}>
                <span>{t('code.details.activeTo')}</span>
              </div>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row`}>
                <span>{codeDetails.activeTo}</span>
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-3' : 'order-1 offset-3'} col-3`}>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mb-2`}>
                <span>{t('code.details.active')}</span>
              </div>
              <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row`}>
                <span className={`fw-bold ${codeDetails.active ? 'text-success' : 'text-danger'} `}>
                  {codeDetails.active ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CodeDetails;
